import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Phone, Mail, MapPin } from "lucide-react";
import { API, COMPANY, FREIGHT_TYPES } from "@/lib/site";

const empty = {
  name: "",
  company: "",
  email: "",
  phone: "",
  freight_type: FREIGHT_TYPES[0],
  origin: "",
  destination: "",
  message: "",
};

const inputCls =
  "w-full rounded-none border-0 border-b border-white/20 bg-transparent px-0 py-3 text-white outline-none transition-colors placeholder:text-[#71717a] focus:border-brand";

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-widest text-[#a1a1a8]">{label}</span>
      {children}
    </label>
  );
}

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setDone(true);
      setForm(empty);
      toast.success("Quote request sent — we'll get back to you shortly.");
    } catch {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="mx-auto max-w-[1600px] px-5 py-24 md:px-10 md:py-36">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-[1fr_1.1fr] lg:gap-24">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-brand">For Shippers · Get a Quote</span>
          <h2 className="mt-4 font-display text-4xl font-black uppercase leading-none tracking-tighter md:text-6xl">
            Let's move
            <br />
            your freight.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#a1a1a8]">
            Tell us about your load and lanes. We'll come back with honest capacity and a fair rate —
            usually the same day.
          </p>

          <div className="mt-12 space-y-px border border-white/10 bg-white/10">
            <a href={COMPANY.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 bg-ink p-5 transition-colors hover:bg-surface">
              <Phone className="h-5 w-5 text-brand" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[#71717a]">Call / Text</div>
                <div className="text-lg">{COMPANY.phone}</div>
              </div>
            </a>
            <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-center gap-4 bg-ink p-5 transition-colors hover:bg-surface">
              <Mail className="h-5 w-5 text-brand" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[#71717a]">Email</div>
                <div className="text-lg break-all">{COMPANY.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 bg-ink p-5">
              <MapPin className="h-5 w-5 text-brand" />
              <div>
                <div className="text-xs uppercase tracking-widest text-[#71717a]">Based In</div>
                <div className="text-lg">{COMPANY.address}, {COMPANY.city}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border border-white/10 bg-surface p-8 md:p-12">
          {done ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="quote-success"
              className="flex h-full flex-col items-start justify-center"
            >
              <CheckCircle2 className="h-12 w-12 text-brand" />
              <h3 className="mt-6 font-display text-3xl font-bold uppercase tracking-tight">Request received</h3>
              <p className="mt-3 max-w-sm text-[#a1a1a8]">
                Thanks — your quote request is on its way to our team. We'll reach out shortly.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-8 border border-white/25 px-6 py-3 text-sm uppercase tracking-wide transition-colors hover:border-brand hover:text-brand"
              >
                Send another
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="flex flex-col gap-5">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Full Name">
                  <input value={form.name} onChange={set("name")} data-testid="quote-name" className={inputCls} placeholder="Your name" />
                </Field>
                <Field label="Company">
                  <input value={form.company} onChange={set("company")} data-testid="quote-company" className={inputCls} placeholder="Company (optional)" />
                </Field>
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Email">
                  <input type="email" value={form.email} onChange={set("email")} data-testid="quote-email" className={inputCls} placeholder="you@email.com" />
                </Field>
                <Field label="Phone">
                  <input value={form.phone} onChange={set("phone")} data-testid="quote-phone" className={inputCls} placeholder="(409) 000-0000" />
                </Field>
              </div>
              <Field label="Freight Type">
                <select value={form.freight_type} onChange={set("freight_type")} data-testid="quote-freight-type" className={inputCls}>
                  {FREIGHT_TYPES.map((t) => <option key={t} className="bg-surface">{t}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Origin">
                  <input value={form.origin} onChange={set("origin")} data-testid="quote-origin" className={inputCls} placeholder="City, State" />
                </Field>
                <Field label="Destination">
                  <input value={form.destination} onChange={set("destination")} data-testid="quote-destination" className={inputCls} placeholder="City, State" />
                </Field>
              </div>
              <Field label="Load Details">
                <textarea value={form.message} onChange={set("message")} data-testid="quote-message" rows={3} className={inputCls} placeholder="Weight, dimensions, pickup date, etc." />
              </Field>
              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit"
                className="group mt-2 flex items-center justify-center gap-2 bg-brand px-8 py-4 text-sm font-semibold uppercase tracking-wide text-ink transition-colors hover:bg-white disabled:opacity-60"
              >
                {loading ? "Sending..." : "Request Quote"}
                {!loading && <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
